import { Container } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetCountdown,
} from "../../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import type { ICountdownTheme } from "./Themes/ICountdownTheme";

import { Theme1Renderer } from "./Themes/Theme1Renderer";

import { SimpleRenderer } from "./Themes/SimpleRenderer";

import { DesignRenderer } from "./Themes/DesignRenderer";

import { getCountdownDesign } from "../../../../../data/countdownDesignsCatalog";

export class CountdownWidgetRenderer {
  private strategy: ICountdownTheme | null = null;

  private lastTheme: string | null = null;

  /**
   * The resolved design id baked into the current `DesignRenderer`, or `null`
   * when the legacy text/Theme1 path is active. Tracked separately from
   * `lastTheme` so the design strategy is re-instantiated when the selected
   * design changes (and only then).
   */

  private lastDesignId: string | null = null;

  render(
    widget: IStudioWidget,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetCountdown).options;

    // ── Design-store path ──────────────────────────────────────────────────

    // When the widget has a resolvable `options.designId`, render the chosen

    // design's CARD SVG (from the local catalog) as a Pixi texture at the

    // widget bounds (see `DesignRenderer`). If the id is absent/unknown we fall

    // through to the legacy text/Theme1 render below, completely untouched.

    //

    // `designId` is an optional, backward-compat field added to the countdown

    // options contract by a sibling task; read it defensively so this renderer

    // compiles regardless of whether that field has landed yet.

    const designId = (options as { designId?: string }).designId as
      | string
      | undefined;

    const design = getCountdownDesign(designId);

    if (design) {
      // Re-instantiate only when switching INTO the design path or to a

      // different design.

      if (this.lastDesignId !== design.id || !this.strategy) {
        this.destroy();

        this.strategy = new DesignRenderer(design);

        this.lastDesignId = design.id;

        this.lastTheme = null;
      }

      this.strategy.render(widget, container, destination);

      return;
    }

    const theme = options.theme || "custom";

    // Leaving the design path (design cleared/unknown) must rebuild a text

    // strategy even if the theme name is unchanged.

    if (
      this.lastTheme !== theme ||
      this.lastDesignId !== null ||
      !this.strategy
    ) {
      this.destroy();

      this.strategy = this.createStrategy(theme);

      this.lastTheme = theme;

      this.lastDesignId = null;
    }

    this.strategy.render(widget, container, destination);
  }

  private createStrategy(theme: string): ICountdownTheme {
    switch (theme) {
      case "theme1":
        return new Theme1Renderer();

      default:
        return new SimpleRenderer();
    }
  }

  public destroy() {
    if (this.strategy) {
      this.strategy.destroy();

      this.strategy = null;
    }

    this.lastTheme = null;

    this.lastDesignId = null;
  }
}
